<template>
  <div class="agreement-confirm">
    <div class="main" :class="{ 'main--has-fixed-submit': !loading && !alreadyConfirmed }">
      <van-loading v-if="loading" class="page-loading" size="24px" vertical>加载中...</van-loading>
      <template v-else>
        <div class="title-block">
          <i class="title-accent" aria-hidden="true" />
          <h1 class="title-text">{{ displayTitle }}</h1>
        </div>
        <div class="rich-wrap">
          <div class="rich-content" v-html="displayContent" />
        </div>

        <!-- 需要签名：横向标签 + 签名区，紧跟富文本下方 -->
        <div v-if="needSign" class="sign-section">
          <div
            v-for="(row, index) in signRowsDisplay"
            :key="index"
            class="sign-row"
          >
            <div class="sign-label">{{ row.alias || '签名' }}</div>
            <div
              class="sign-box"
              :class="{
                'is-filled': alreadyConfirmed ? !!confirmSignUrls[index] : !!signatureImages[index],
                'is-readonly': alreadyConfirmed
              }"
              role="button"
              tabindex="0"
              @click="onSignBoxClick(index)"
            >
              <template v-if="alreadyConfirmed && confirmSignUrls[index]">
                <img
                  :src="confirmSignUrls[index]"
                  class="sign-preview"
                  alt=""
                >
              </template>
              <template v-else-if="signatureImages[index]">
                <img
                  :src="signatureImages[index]"
                  class="sign-preview"
                  alt=""
                >
              </template>
              <template v-else>
                <van-icon name="edit" class="sign-box-icon" />
                <span class="sign-box-text">去签名</span>
              </template>
            </div>
          </div>
          <div v-if="alreadyConfirmed && confirmTimeDisplay" class="confirm-time-line">
            确认时间：{{ confirmTimeDisplay }}
          </div>
        </div>

        <!-- 不需要签名：阅读确认 -->
        <div v-else class="agree-section">
          <template v-if="alreadyConfirmed">
            <div class="agree-readonly">
              <span class="agree-text">
                我已阅读并同意
                <span class="agree-name">《{{ displayTitle }}》</span>
              </span>
            </div>
            <div v-if="confirmTimeDisplay" class="confirm-time-line agree-confirm-time">
              确认时间：{{ confirmTimeDisplay }}
            </div>
          </template>
          <van-checkbox v-else v-model="agreed" shape="square" icon-size="18px">
            <span class="agree-text">
              我已阅读并同意
              <span class="agree-name">《{{ displayTitle }}》</span>
            </span>
          </van-checkbox>
        </div>
      </template>
    </div>

    <div v-if="!loading && !alreadyConfirmed" class="submit-footer">
      <van-button
        round
        block
        type="info"
        class="submit-btn"
        :disabled="submitting"
        :loading="submitting"
        @click="onSubmitClick"
      >提交</van-button>
    </div>

    <van-popup
      v-model="showSignPad"
      position="bottom"
      round
      :close-on-click-overlay="false"
      class="sign-pad-popup"
      get-container="body"
      @opened="onSignPadOpened"
      @closed="onSignPadClosed"
    >
      <div class="sign-pad-sheet">
        <div class="sign-pad-head">
          <span class="sign-pad-label">{{ activeSignLabel }}</span>
          <span class="sign-pad-clear" @click="clearSignaturePad">清空</span>
        </div>
        <div
          ref="signatureWrap"
          class="signature-canvas-wrap"
        >
          <canvas
            ref="signatureCanvas"
            class="signature-canvas"
          />
        </div>
        <div class="sign-pad-actions">
          <van-button
            round
            plain
            hairline
            class="sign-pad-btn sign-pad-btn--cancel"
            @click="onSignCancel"
          >取消</van-button>
          <van-button
            round
            type="info"
            class="sign-pad-btn sign-pad-btn--ok"
            @click="onSignConfirm"
          >确认</van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Vue from 'vue'
import { NavBar, Loading, Checkbox, Button, Icon, Toast, Popup, Dialog } from 'vant'
import SignaturePad from 'signature_pad'
import {
  getBMMatchTeamAgreementInfo,
  getBMMatchTeamPlayerAgreementInfo,
  confirmBMMatchTeamAgreement,
  confirmBMMatchTeamPlayerAgreement
} from '@/api/match'
import { getQiniuUploadToken, uploadImage } from '@/api/qiniu'
import { ConstConfig } from '@/constants'

Vue.use(NavBar).use(Loading).use(Checkbox).use(Button).use(Icon).use(Toast).use(Popup).use(Dialog)

export default {
  name: 'AgreementConfirm',
  data() {
    return {
      loading: true,
      listType: 'leader',
      matchId: '',
      teamId: '',
      unionMatchId: '',
      playerId: '',
      agreementId: '',
      detail: {
        title: '',
        content: '',
        signRequired: 0,
        signFields: [],
        confirmed: 0,
        confirmedInfo: null
      },
      agreed: false,
      /** 各签名位对应的 PNG dataURL，下标与 signRowsDisplay 一致 */
      signatureImages: {},
      showSignPad: false,
      activeSignIndex: 0,
      signaturePad: null,
      submitting: false
    }
  },
  computed: {
    isPlayer() {
      return this.listType === 'player'
    },
    displayTitle() {
      return this.detail.title || '协议'
    },
    displayContent() {
      return this.detail.content || ''
    },
    /** signRequired：1 需要签名 */
    needSign() {
      return Number(this.detail.signRequired) === 1
    },
    signRowsDisplay() {
      const rows = this.detail.signFields
      if (Array.isArray(rows) && rows.length) {
        return rows.map(r => ({
          alias: (r && r.alias) || '签名'
        }))
      }
      if (this.needSign) {
        return [{ alias: '签名' }]
      }
      return []
    },
    activeSignLabel() {
      const rows = this.signRowsDisplay
      const row = rows[this.activeSignIndex]
      return (row && row.alias) || '签名'
    },
    /** 可提交：需签名时全部签完；仅阅读时需勾选同意 */
    canSubmit() {
      if (this.alreadyConfirmed) return false
      if (this.needSign) {
        const n = this.signRowsDisplay.length
        if (!n) return false
        for (let i = 0; i < n; i++) {
          if (!this.signatureImages[i]) return false
        }
        return true
      }
      return this.agreed
    },
    /** 接口已确认：confirmed=1 或存在 confirmedInfo */
    alreadyConfirmed() {
      if (Number(this.detail.confirmed) === 1) return true
      const ci = this.detail.confirmedInfo
      if (ci && (ci.confirmTime != null && ci.confirmTime !== '')) return true
      if (ci && ci.signPics != null && String(ci.signPics).trim() !== '') return true
      return false
    },
    /** 七牛 key 列表，与签名位顺序一致 */
    confirmSignUrls() {
      const ci = this.detail.confirmedInfo
      if (!ci || ci.signPics == null || String(ci.signPics).trim() === '') return {}
      const keys = String(ci.signPics)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      const base = (ConstConfig.qiniuCdn || '').replace(/\/$/, '')
      const out = {}
      keys.forEach((key, index) => {
        if (/^https?:\/\//i.test(key)) {
          out[index] = key
        } else {
          out[index] = `${base}/${key}`
        }
      })
      return out
    },
    /** 展示用：2026-03-01 15:00 */
    confirmTimeDisplay() {
      const t =
        (this.detail.confirmedInfo && this.detail.confirmedInfo.confirmTime) || ''
      return this.formatConfirmTimeForDisplay(t)
    }
  },
  beforeDestroy() {
    this.destroySignaturePad()
  },
  mounted() {
    const q = this.$route.query || {}
    const t = (q.type || 'leader').toString().toLowerCase()
    if (t === 'player' || t === 'member') this.listType = 'player'
    else this.listType = 'leader'
    this.matchId = q.matchId || ''
    this.teamId = q.teamId || ''
    this.unionMatchId = q.unionMatchId || ''
    this.playerId = q.playerId || ''
    this.agreementId = q.agreementId || ''
    this.fetchDetail()
  },
  methods: {
    onBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace({
          path: '/entry-page/agreement-list',
          query: {
            type: this.isPlayer ? 'player' : 'leader',
            unionMatchId: this.unionMatchId,
            matchId: this.matchId,
            teamId: this.teamId,
            ...(this.isPlayer && this.playerId ? { playerId: this.playerId } : {})
          }
        })
      }
    },
    normalizeDetail(payload) {
      const d = payload || {}
      const a = d.agreement || d
      const title = a.title != null ? a.title : d.title
      const content = a.content != null ? a.content : d.content
      let signRequired = a.signRequired != null ? a.signRequired : d.signRequired
      if (signRequired == null) signRequired = 0
      let signFields = a.signFields != null ? a.signFields : d.signFields
      if (!Array.isArray(signFields)) signFields = []
      let confirmed = a.confirmed != null ? a.confirmed : d.confirmed
      let confirmedInfo = d.confirmedInfo != null ? d.confirmedInfo : a.confirmedInfo
      if (confirmedInfo != null && typeof confirmedInfo === 'object') {
        confirmedInfo = { ...confirmedInfo }
      } else {
        confirmedInfo = null
      }
      if ((confirmed === undefined || confirmed === null) && confirmedInfo) {
        confirmed = 1
      }
      return {
        title: title != null ? String(title) : '',
        content: content != null ? String(content) : '',
        signRequired: Number(signRequired) || 0,
        signFields,
        confirmed: Number(confirmed) || 0,
        confirmedInfo
      }
    },
    fetchDetail() {
      if (!this.matchId || !this.teamId || !this.agreementId) {
        this.loading = false
        Toast.fail('缺少必要参数')
        return
      }
      if (this.isPlayer && !this.playerId) {
        this.loading = false
        Toast.fail('缺少队员参数')
        return
      }
      const base = {
        matchId: this.matchId,
        teamId: this.teamId,
        agreementId: Number(this.agreementId) || this.agreementId
      }
      const req = this.isPlayer
        ? getBMMatchTeamPlayerAgreementInfo({ ...base, playerId: this.playerId })
        : getBMMatchTeamAgreementInfo(base)
      this.loading = true
      req
        .then(res => {
          if (!res) {
            this.detail = {
              title: '',
              content: '',
              signRequired: 0,
              signFields: [],
              confirmed: 0,
              confirmedInfo: null
            }
            return
          }
          this.detail = this.normalizeDetail(res.data || {})
        })
        .finally(() => {
          this.loading = false
        })
    },
    destroySignaturePad() {
      if (this.signaturePad) {
        try {
          this.signaturePad.off()
        } catch (e) {
          /* noop */
        }
        this.signaturePad = null
      }
    },
    setupSignatureCanvas() {
      this.destroySignaturePad()
      const canvas = this.$refs.signatureCanvas
      const wrap = this.$refs.signatureWrap
      if (!canvas || !wrap) return
      const w = wrap.clientWidth
      const h = wrap.clientHeight
      if (!w || !h) return
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = w * ratio
      canvas.height = h * ratio
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      const ctx = canvas.getContext('2d')
      if (ctx) ctx.scale(ratio, ratio)
      this.signaturePad = new SignaturePad(canvas, {
        backgroundColor: 'rgb(239, 239, 239)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 0.6,
        maxWidth: 2.5
      })
    },
    formatConfirmTimeForDisplay(iso) {
      if (iso == null || iso === '') return ''
      const s = String(iso).trim()
      const d = new Date(s.replace(/\+0800$/, '+08:00'))
      if (isNaN(d.getTime())) return s
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${day} ${h}:${min}`
    },
    onSignBoxClick(index) {
      if (this.alreadyConfirmed) return
      this.openSignPad(index)
    },
    openSignPad(index) {
      this.activeSignIndex = index
      this.showSignPad = true
    },
    onSignPadOpened() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.setupSignatureCanvas()
        })
      })
    },
    onSignPadClosed() {
      this.destroySignaturePad()
    },
    /** 清空当前签名画布 */
    clearSignaturePad() {
      if (this.signaturePad && typeof this.signaturePad.clear === 'function') {
        this.signaturePad.clear()
      }
    },
    onSignCancel() {
      this.showSignPad = false
    },
    onSignConfirm() {
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        Toast.fail('请先完成签名')
        return
      }
      const dataUrl = this.signaturePad.toDataURL('image/png')
      this.$set(this.signatureImages, this.activeSignIndex, dataUrl)
      this.showSignPad = false
    },
    /** PNG dataURL → Blob，用于七牛上传 */
    dataURLToBlob(dataURL) {
      const parts = dataURL.split(',')
      const mime = (parts[0].match(/:(.*?);/) || [])[1] || 'image/png'
      const raw = window.atob(parts[1])
      const len = raw.length
      const arr = new Uint8Array(len)
      for (let i = 0; i < len; i++) arr[i] = raw.charCodeAt(i)
      return new Blob([arr], { type: mime })
    },
    /** 提交：需签名时先传七牛，key 逗号拼接为 signPics */
    async onSubmitClick() {
      if (!this.canSubmit) {
        if (this.needSign) {
          Dialog.alert({
            message: '提交前请先完成签名',
            confirmButtonText: '确定'
          })
        } else {
          Dialog.alert({
            message: '提交前请先确认协议',
            confirmButtonText: '确定'
          })
        }
        return
      }
      if (this.submitting) return
      this.submitting = true
      try {
        let signPics = ''
        if (this.needSign) {
          const tokenRes = await getQiniuUploadToken()
          if (!tokenRes || !tokenRes.data || !tokenRes.data.token) {
            Toast.fail('获取上传凭证失败')
            return
          }
          const qiniuToken = tokenRes.data.token
          const keys = []
          for (let i = 0; i < this.signRowsDisplay.length; i++) {
            const dataUrl = this.signatureImages[i]
            if (!dataUrl) {
              Toast.fail('请完成全部签名')
              return
            }
            const formData = new FormData()
            formData.append('token', qiniuToken)
            formData.append('file', this.dataURLToBlob(dataUrl), `sign_${i}.png`)
            const upRes = await uploadImage(formData)
            const key = upRes && (upRes.key || upRes.hash)
            if (!key) {
              Toast.fail('签名图片上传失败')
              return
            }
            keys.push(key)
          }
          signPics = keys.join(',')
        }

        const agreementId = Number(this.agreementId) || this.agreementId
        const payload = {
          matchId: this.matchId,
          teamId: this.teamId,
          agreementId
        }
        if (this.needSign) payload.signPics = signPics

        const res = this.isPlayer
          ? await confirmBMMatchTeamPlayerAgreement({
            ...payload,
            playerId: this.playerId
          })
          : await confirmBMMatchTeamAgreement(payload)

        if (res) {
          Toast.success('提交成功')
          if (window.history.length > 1) {
            this.$router.back()
          } else {
            this.$router.replace({
              path: '/entry-page/agreement-list',
              query: {
                type: this.isPlayer ? 'player' : 'leader',
                unionMatchId: this.unionMatchId,
                matchId: this.matchId,
                teamId: this.teamId,
                ...(this.isPlayer && this.playerId ? { playerId: this.playerId } : {})
              }
            })
          }
        }
      } catch (e) {
        /* 业务错误已在 request 拦截器 Toast；网络异常由 axios reject */
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.agreement-confirm {
  min-height: 100vh;
  background: #f5f6f7;
  box-sizing: border-box;
}

.main {
  padding: 12px 16px calc(24px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  &.main--has-fixed-submit {
    /* 为固底提交栏留出空间，避免内容被遮挡 */
    padding-bottom: calc(76px + env(safe-area-inset-bottom));
  }
}

.submit-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding: 12px 16px calc(24px + env(safe-area-inset-bottom));
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

.page-loading {
  padding: 48px 0;
  display: flex;
  justify-content: center;
}

.title-block {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}

.title-accent {
  width: 4px;
  min-height: 22px;
  margin-right: 10px;
  margin-top: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #7c5cff 0%, #5b4fcf 100%);
  flex-shrink: 0;
}

.title-text {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  flex: 1;
  word-break: break-all;
}

.rich-wrap {
  background: #fafafa;
  border-radius: 8px;
  padding: 14px 12px;
}

.rich-content {
  font-size: 15px;
  color: #333;
  line-height: 1.65;
  word-break: break-word;

  ::v-deep p {
    margin: 0 0 12px;
  }
  ::v-deep p:last-child {
    margin-bottom: 0;
  }
  ::v-deep strong,
  ::v-deep b {
    font-weight: 600;
  }
}

.sign-section {
  margin-top: 20px;
  margin-bottom: 16px;
}

.sign-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  &:last-child {
    margin-bottom: 0;
  }
}

.sign-label {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
  word-break: break-word;
}

.sign-box {
  flex: 0 0 200px;
  width: 200px;
  max-width: 200px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 6px 8px;
  border: 1px dashed #509dea;
  border-radius: 8px;
  background: #fff;
  color: #1989fa;
  font-size: 15px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &.is-filled {
    border-style: solid;
    border-color: #c8e0fb;
    background: #fafcff;
    padding: 4px;
  }
  .sign-box-icon {
    margin-right: 6px;
    font-size: 18px;
  }
  .sign-box-text {
    font-weight: 500;
  }
  &.is-readonly {
    cursor: default;
    pointer-events: none;
    border-style: solid;
    border-color: #c8e0fb;
    background: #fafcff;
  }
}

.confirm-time-line {
  margin-top: 16px;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  line-height: 1.5;
  text-align: right;
}

.agree-readonly {
  .agree-text {
    font-size: 14px;
    color: #606266;
    line-height: 1.5;
  }
}

.agree-confirm-time {
  margin-top: 12px;
}

.sign-preview {
  display: block;
  max-width: 100%;
  max-height: 72px;
  object-fit: contain;
}

.sign-pad-popup {
  max-height: 85vh;
}

.sign-pad-sheet {
  padding: 16px 16px calc(20px + env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.sign-pad-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.sign-pad-label {
  font-size: 15px;
  color: #303133;
  font-weight: 500;
}

.sign-pad-clear {
  flex-shrink: 0;
  font-size: 15px;
  color: #1989fa;
  cursor: pointer;
  user-select: none;
}

.signature-canvas-wrap {
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background: #efefef;
}

.signature-canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
}

.sign-pad-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
}

.sign-pad-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
}

.sign-pad-btn--cancel {
  color: #646566;
  border-color: #dcdee0;
  background: #fff;
}

.sign-pad-btn--ok {
  border: none;
}

.agree-section {
  margin-top: 20px;
  background: #eee;
  border-radius: 8px;
  padding: 14px 12px;
  margin-bottom: 16px;
}

.agree-text {
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
}

.agree-name {
  color: #1989fa;
}

.submit-btn {
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  width: 132px;
  float: right;
}
</style>
